/**
 * Создание UUID-ключа.
 *
 * Главное поле — `label` (имя, для кого этот ключ). user_id опционален:
 *   - указан → подписка привязывается к этому tg_id
 *   - не указан → бэкенд создаёт «анонимного» юзера с tg_id < 0
 *     (тестовый доступ для друга/коллеги без Telegram)
 *
 * После создания — модалка с sub-URL, QR-кодом и инструкцией.
 */
import { Create, useForm } from "@refinedev/antd";
import { useOne } from "@refinedev/core";
import {
  Form, Input, InputNumber, Select, Alert, Modal, Typography,
  Space, Button, Tag,
} from "antd";
import { EyeOutlined, UserOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { TARIFF_LABEL, type Tariff } from "../../types";
import { SubUrlBlock } from "../../components/SubUrlBlock";
import { CopyAccessButton } from "../../components/CopyAccessButton";

interface CreatePayload {
  label: string;
  user_id?: number | null;
  tariff: Tariff;
  days: number;
}

interface CreateResponse {
  id: number;
  user_id: number;
  sub_token: string;
  sub_url: string;
  end_at: string;
}

export const SubscriptionCreate = () => {
  const navigate = useNavigate();
  const [created, setCreated] = useState<CreateResponse | null>(null);
  const [searchParams] = useSearchParams();
  const presetUserIdRaw = searchParams.get("user_id");
  const presetUserId = presetUserIdRaw ? Number(presetUserIdRaw) : null;

  // Подгружаем юзера если зашли с ?user_id= (из карточки юзера)
  const { data: userData } = useOne<{
    tg_id: number; username: string | null;
    first_name: string | null; last_name: string | null;
  }>({
    resource: "users",
    id: presetUserId ?? 0,
    queryOptions: { enabled: presetUserId !== null && presetUserId > 0 },
  });
  const presetUser = userData?.data;

  const { formProps, saveButtonProps, form } = useForm<CreateResponse, any, CreatePayload>({
    resource: "subscriptions",
    action: "create",
    redirect: false,
    onMutationSuccess: ({ data }) => setCreated(data as CreateResponse),
  });

  // При получении данных юзера — подставляем в форму
  useEffect(() => {
    if (presetUserId !== null) {
      form.setFieldValue("user_id", presetUserId);
    }
    if (presetUser && !form.getFieldValue("label")) {
      const suggested =
        presetUser.first_name ||
        (presetUser.username ? `@${presetUser.username}` : `tg:${presetUser.tg_id}`);
      form.setFieldValue("label", suggested);
    }
  }, [presetUserId, presetUser, form]);

  const subUrl = created?.sub_url ?? "";

  return (
    <>
      <Create
        title={presetUser
          ? `Создать UUID для ${presetUser.first_name || presetUser.username || `tg:${presetUser.tg_id}`}`
          : "Создать UUID-ключ"}
        breadcrumb={false}
        saveButtonProps={{ ...saveButtonProps, children: "Создать UUID" }}
      >
        {presetUser ? (
          <Alert
            type="success"
            showIcon
            icon={<UserOutlined />}
            style={{ marginBottom: 16 }}
            message={
              <Space wrap>
                <span>UUID создаётся для:</span>
                <Tag color="blue" style={{ fontSize: 13, padding: "2px 8px" }}>
                  {presetUser.first_name || "без имени"}
                  {presetUser.last_name ? ` ${presetUser.last_name}` : ""}
                  {presetUser.username ? ` · @${presetUser.username}` : ""}
                  {" · "}tg:{presetUser.tg_id}
                </Tag>
              </Space>
            }
            description="Telegram ID и предлагаемое имя уже подставлены ниже — можно изменить, если нужно."
          />
        ) : (
          <Alert
            type="info" showIcon style={{ marginBottom: 16 }}
            message="Бесплатный тест-доступ"
            description="Подписка создаётся без оплаты. Достаточно указать имя — Telegram ID не обязателен. Если получатель не привязан к боту, мы создадим для него анонимный аккаунт автоматически."
          />
        )}

        <Form {...formProps} layout="vertical"
              initialValues={{ days: 7, tariff: "basic" }}>
          <Form.Item
            label="Имя получателя ключа"
            name="label"
            extra={presetUser
              ? "Подставлено автоматически из профиля юзера. Можно изменить."
              : "Любая метка, чтобы потом найти этот UUID — например «Петя из Иркутска» или «тестер #2»."}
            rules={[{ required: true, min: 1, max: 64 }]}
          >
            <Input placeholder="Имя или метка" autoFocus={!presetUser} />
          </Form.Item>

          <Form.Item
            label={presetUser ? "Telegram ID (из карточки юзера)" : "Telegram ID (необязательно)"}
            name="user_id"
            extra={presetUser
              ? "Подставлено из карточки юзера автоматически."
              : "Если знаете — укажите. Если нет — оставьте пустым, мы создадим анонимный аккаунт."}
          >
            <InputNumber style={{ width: "100%" }}
                         placeholder="например 433340679"
                         disabled={!!presetUser} />
          </Form.Item>

          <Form.Item label="Тариф" name="tariff" rules={[{ required: true }]}>
            <Select options={(["basic", "parental", "premium"] as Tariff[]).map(t => ({
              value: t, label: TARIFF_LABEL[t],
            }))} />
          </Form.Item>

          <Form.Item label="Срок действия (дни)" name="days"
                     rules={[{ required: true, type: "number", min: 1, max: 365 }]}>
            <InputNumber min={1} max={365} style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Create>

      <Modal
        title={created && `✅ UUID-ключ для «${formProps.form?.getFieldValue("label")}» создан`}
        open={created !== null}
        footer={null}
        closable={false}
        width={680}
      >
        {created && (
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <Alert type="success" showIcon
                   message={`Подписка #${created.id} активна до ${
                     new Date(created.end_at).toLocaleDateString("ru-RU")}`} />

            <CopyAccessButton subUrl={subUrl} />

            <SubUrlBlock subUrl={subUrl} />

            <Space style={{ width: "100%", justifyContent: "space-between" }}>
              <Button icon={<EyeOutlined />}
                      onClick={() => navigate(`/subscriptions/${created.id}`)}>
                Открыть карточку
              </Button>
              <Space>
                <Button onClick={() => setCreated(null)}>
                  Создать ещё
                </Button>
                <Button type="primary"
                        onClick={() => navigate("/subscriptions")}>
                  К списку
                </Button>
              </Space>
            </Space>
          </Space>
        )}
      </Modal>
    </>
  );
};
